// Phosphor icons chosen in the dashboard are stored as "i-ph-<name>;<color>"
// (category icons) or "::i-ph-<name>;<color>::" (article markers). Those names
// are not visible to Tailwind's content scanner, so they are rendered as inline
// SVG from the per-style packs in config/phosphor/ (generated by
// `rake phosphor:generate`). Literal `i-ph-*` classes in source files keep using
// the CSS rules that tailwindcss-icons emits for them.
const NAME_RE = /^i-ph-([a-z0-9]+(?:-[a-z0-9]+)*)$/;
const COLOR_RE = /^(?:#[0-9a-f]{3,8}|[a-z]+)$/i;
const STYLE_RE = /-(thin|light|bold|fill|duotone)$/;

// Static specifiers so Rollup emits one lazy chunk per style.
const PACK_LOADERS = {
  regular: () => import('phosphor/ph-regular.json'),
  thin: () => import('phosphor/ph-thin.json'),
  light: () => import('phosphor/ph-light.json'),
  bold: () => import('phosphor/ph-bold.json'),
  fill: () => import('phosphor/ph-fill.json'),
  duotone: () => import('phosphor/ph-duotone.json'),
};

const packPromises = new Map(); // style -> Promise<{ [name]: body }>
const bodyCache = new Map(); // name -> body

// True for the value the icon picker persists ("i-ph-<name>;<color>").
export const isStoredPhosphorIcon = value =>
  typeof value === 'string' && /^i-ph-[a-z0-9-]+;/.test(value);

// 'i-ph-rocket-duotone;#4945FF' -> { name: 'rocket-duotone', color: '#4945FF' },
// or null when the name is not a phosphor icon. An invalid colour is dropped.
export const parsePhosphorIcon = value => {
  if (typeof value !== 'string') return null;
  const [rawName, rawColor = ''] = value.split(';');
  const match = NAME_RE.exec(rawName.trim());
  if (!match) return null;
  const color = rawColor.trim();
  return { name: match[1], color: COLOR_RE.test(color) ? color : '' };
};

export const phosphorStyleOf = name => STYLE_RE.exec(name)?.[1] ?? 'regular';

export const loadPhosphorPack = style => {
  if (!PACK_LOADERS[style]) return Promise.resolve({});
  if (!packPromises.has(style)) {
    packPromises.set(
      style,
      PACK_LOADERS[style]().then(mod => {
        // Vite exposes the JSON as both a default export and named keys.
        const icons = (mod.default ?? mod).icons || {};
        Object.entries(icons).forEach(([name, body]) =>
          bodyCache.set(name, body)
        );
        return icons;
      })
    );
  }
  return packPromises.get(style);
};

export const getCachedPhosphorBody = name => bodyCache.get(name) ?? null;

export const getPhosphorBody = async name => {
  if (bodyCache.has(name)) return bodyCache.get(name);
  const icons = await loadPhosphorPack(phosphorStyleOf(name));
  return icons[name] ?? '';
};

// Same markup as PhosphorIcon.render in Ruby.
export const phosphorSvgMarkup = body =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="1em" height="1em" fill="currentColor" aria-hidden="true">${body}</svg>`;
